/**
 * The WebView bridge between the React UI and the native Android shell (ui-overhaul §10).
 * Messages are JSON `{id, method, params}` answered by `{id, result}` or `{id, error}`, plus
 * native-to-JS events. Tokens never cross this boundary: `api.request` carries a path and body,
 * and native adds the bearer token and enforces HTTPS. Shared by React and the Kotlin tests.
 */

export type ApiRequest = {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Absolute path on the API origin, e.g. `/v1/me`. Never a full URL. */
  path: string;
  body?: unknown;
  idempotencyKey?: string;
};
export type ApiResponse = { status: number; body: unknown };

export type BridgeMethods = {
  "api.request": { params: ApiRequest; result: ApiResponse };
  "auth.state": { params: Record<string, never>; result: { signedIn: boolean } };
  "auth.signIn": { params: Record<string, never>; result: { signedIn: boolean } };
  "auth.signOut": { params: Record<string, never>; result: { signedIn: false } };
  "keyboard.hide": { params: Record<string, never>; result: null };
  "share.invite": { params: { url: string; title: string }; result: { shared: boolean } };
  "device.network": {
    params: Record<string, never>;
    result: { route: "WIFI" | "CELLULAR" | "NONE" | "OTHER" };
  };
};

/** One tile of the phone's Sync grid: media taken inside the trip window, not yet uploaded. */
export type SyncTile = {
  /** Hashed id; the content URI never leaves native. Thumbnail: `/thumbs/<id>.jpg`. */
  id: string;
  kind: "PHOTO" | "VIDEO";
  sizeBytes: number;
  capturedAt: string;
  checked: boolean;
  /** The switched-on folder it comes from (see SyncSource). */
  sourceId: string;
};
/** What decides a source's icon. */
export type SourceKind = "CAMERA" | "SCREEN" | "SCREENSHOTS" | "CARD" | "FOLDER";
/** A folder on the phone that the person shares with the trip; the path never leaves native. */
export type SyncSource = {
  /** Hashed id (20 hex characters). */
  id: string;
  name: string;
  kind: SourceKind;
  /** On a memory card. */
  removable: boolean;
  /** False while its memory card is out. */
  connected: boolean;
};
/** One row of the Sync sources screen. Counts are files from inside the trip window. */
export type SourceFolder = SyncSource & {
  on: boolean;
  /** Added by the person with "Add a folder". */
  picked: boolean;
  /** Files from the trip dates in this folder, and how many of those are not uploaded yet. */
  files: number;
  bytes: number;
  newFiles: number;
  newBytes: number;
};
export type SyncItemStatus =
  | "QUEUED"
  | "HASHING"
  | "UPLOADING"
  | "VERIFYING"
  | "VERIFIED"
  /** Refused because the trip already has these exact bytes (same SHA-256); nothing was uploaded. */
  | "DUPLICATE"
  | "FAILED"
  | "PAUSED"
  | "WAITING_SOURCE";
export type SyncState = {
  phase: "IDLE" | "SCANNING" | "CONFIRM" | "SYNCING" | "DONE" | "ERROR";
  tripId: string | null;
  message: string | null;
  items: { id: string; kind: string; sizeBytes: number; status: SyncItemStatus }[];
  verified: number;
  failed: number;
};
export type SourcesList =
  | { tripId: string; blocked: "NOT_STARTED" | "NO_CONSENT" | "NO_PERMISSION" }
  | {
      tripId: string;
      blocked?: undefined;
      items: SyncTile[];
      /** Switched-on sources, in display order; tiles point at them by id. */
      sources: SyncSource[];
      /** A memory card is in the phone right now, and its name. */
      card: { mounted: boolean; name: string | null };
      unsupported: number;
      resumable: number;
      sync: SyncState;
    };

export type SyncMethods = {
  "sources.list": { params: { tripId: string }; result: SourcesList };
  "sources.toggle": {
    params: { tripId: string; id: string; checked: boolean };
    result: { id: string; checked: boolean };
  };
  "sources.pickManual": {
    params: { tripId: string };
    result: { picked: number; unsupported?: number; sync?: SyncState };
  };
  /** Every folder with media, switched on or off, for the Sync sources screen. */
  "sources.folders": {
    params: { tripId: string };
    result:
      { tripId: string; folders: SourceFolder[] } | { tripId: string; blocked: "NO_PERMISSION" };
  };
  "sources.setFolder": {
    params: { tripId: string; sourceId: string; on: boolean };
    result: { sourceId: string; on: boolean };
  };
  /** Opens the system folder picker; the chosen folder is shared from then on. */
  "sources.addFolder": {
    params: { tripId: string };
    result: { added: SyncSource | null };
  };
  "sync.start": { params: { tripId: string }; result: SyncState };
  "sync.state": { params: Record<string, never>; result: SyncState };
  "sync.stop": { params: Record<string, never>; result: SyncState };
  /**
   * Starts recording the route. Asks for location while in use, then for "Allow all the time" so the
   * route keeps being recorded with the app closed. Never returns coordinates.
   */
  "location.start": {
    params: { tripId: string };
    result: LocationState & { permission: "ALWAYS" | "WHILE_IN_USE" | "DENIED" };
  };
  "location.state": { params: Record<string, never>; result: LocationState };
  "location.stop": { params: Record<string, never>; result: { active: false } };
  /** Asks again for "Allow all the time" (Android shows its settings page for it). */
  "location.allowAlways": { params: Record<string, never>; result: LocationState };
  /**
   * Where this phone is, and the part of its route still on the device.
   *
   * The one location call that does return coordinates, and the exception is deliberate: they go
   * to the app's own bundled page at [BRIDGE_ORIGIN] and no further, so the traveller can see
   * themselves on their own map and watch the trail they are laying down before it reaches the
   * server. Nothing here is logged, and a page served from anywhere else cannot ask — the origin
   * allowlist is what makes this safe. Every other location method still answers on/off only.
   */
  "location.here": { params: Record<string, never>; result: LocalTrack };
  /** What this phone still has to allow before a route records itself unattended. */
  "device.readiness": { params: Record<string, never>; result: DeviceReadiness };
  /**
   * Puts one requirement to the system — the runtime permission, or the battery dialog — and
   * answers with the report read again afterwards, so a dialog dismissed without a choice reports
   * what it really is rather than success. Only worth calling while the state is `MISSING`.
   */
  "device.request": { params: { requirement: Requirement }; result: DeviceReadiness };
  /** Opens the phone's own screen for a requirement, for the ones Android will no longer ask about. */
  "device.openSettings": { params: { requirement: Requirement }; result: DeviceReadiness };
  /** Records that the person says they turned on something the app cannot read. Their word. */
  "device.acknowledge": { params: { requirement: Requirement }; result: DeviceReadiness };
  /**
   * Takes that word back, so the question returns. A manufacturer's switch can be turned off
   * again — ColorOS commonly resets it after a system update — and a screen that still showed an
   * old "I did this" with nothing to press would be stating something it never measured.
   */
  "device.forget": { params: { requirement: Requirement }; result: DeviceReadiness };
};
/** [background]: "Allow all the time" is granted, so recording continues with the app closed. */
export type LocationState = { active: boolean; tripId: string | null; background?: boolean };

/**
 * One thing a phone has to allow for a route to record itself while nobody is watching.
 *
 * `oemAutoStart` is the manufacturer's own auto-start switch. On the layers that kill background
 * apps it is the setting that decides whether recording ever comes back, and it is also the one no
 * app can read: measured on ColorOS 15, both its provider and its settings screen require a
 * signature permission. That is why it has states of its own below.
 */
export type Requirement =
  | "notifications"
  | "location"
  | "locationAlways"
  | "batteryOptimisation"
  | "backgroundUnrestricted"
  | "media"
  | "oemAutoStart";

/**
 * How one requirement stands. Only `OK` means the system itself confirmed it.
 *
 * `MISSING` is read from the system and asking again still brings up a dialog; `BLOCKED` is read
 * from the system and it will not ask again, so only its settings screen can change it.
 * `PARTIAL` is read from the system and granted for less than is needed — Android 14's "Select
 * photos", where the trip sees only hand-picked items — and asking again only re-opens the picker.
 * `ACKNOWLEDGED` and `UNVERIFIABLE` belong to a requirement that cannot be read at all — the
 * person's word that they did it, or nothing said yet. Neither may ever be drawn as granted.
 */
export type ReadinessState =
  "OK" | "MISSING" | "BLOCKED" | "PARTIAL" | "ACKNOWLEDGED" | "UNVERIFIABLE";

export type ReadinessCheck = { id: Requirement; state: ReadinessState };

/**
 * [complete] counts only the requirements the system can report; what cannot be read can never
 * hold it back, or a phone with no manufacturer startup manager could never finish setting up.
 */
export type DeviceReadiness = { checks: ReadinessCheck[]; complete: boolean };

/** One fix as the phone recorded it: epoch ms, degrees, metres. */
export type LocalFix = { t: number; lat: number; lon: number; accuracyM: number };
/**
 * What the phone's zone watcher made of the newest fix: the danger map it is on (walking or
 * riding, held through the speed dead zone), the sector it is in and that sector's adjusted level.
 * [sector] and [level] are null outside every sector. The same reading the zone notifications
 * show, so the page never draws one mode while the phone buzzes the other.
 */
export type LocalZone = { mode: "FOOT" | "VEHICLE"; sector: string | null; level: number | null };
/**
 * [here] is the newest fix, null until one lands. [pending] is what this device has recorded and
 * the server has not accepted yet, oldest first and capped — the freshest part of the route, and
 * on a phone that has been offline the only copy of it. [zone] is null while the trip's map
 * layers are off and before the first fix, and absent from a shell that predates it.
 */
export type LocalTrack = { here: LocalFix | null; pending: LocalFix[]; zone?: LocalZone | null };

/** Events native pushes to the page: `{event, data}`. */
export type BridgeEvents = {
  "auth.changed": { signedIn: boolean };
  "sync.changed": SyncState;
  "location.changed": LocationState;
  /**
   * What this phone needs, re-read. Sent when the app comes back to the front, because most of
   * these are changed on a screen of the system's own that answers nothing back to the app.
   */
  "device.readiness": DeviceReadiness;
  /** A fix landed: the newest position and the pending trail, same shape as `location.here`. */
  "location.moved": LocalTrack;
  "network.changed": { route: "WIFI" | "CELLULAR" | "NONE" | "OTHER" };
};

/** The JavaScript object name native injects (WebViewCompat.addWebMessageListener). */
export const BRIDGE_OBJECT = "tripNative";
/** The only origin allowed to use it (WebViewAssetLoader's app origin). */
export const BRIDGE_ORIGIN = "https://appassets.androidplatform.net";
export type AllBridgeMethods = BridgeMethods & SyncMethods;
export type BridgeMethod = keyof AllBridgeMethods;

export type BridgeRequest<M extends BridgeMethod = BridgeMethod> = {
  id: string;
  method: M;
  params: AllBridgeMethods[M]["params"];
};
export type BridgeReply =
  { id: string; result: unknown } | { id: string; error: { code: string; message: string } };
export type BridgeEvent = { event: string; data: unknown };

/** Paths the bridge forwards: API paths only, no scheme, host, traversal or fragments. */
export function isBridgeApiPath(path: string): boolean {
  return (
    /^\/v1\/[A-Za-z0-9/_\-.~%]*(\?[A-Za-z0-9_\-.~%&=+,:]*)?$/.test(path) && !path.includes("..")
  );
}
